import { useState, useCallback, useRef } from 'react'
import { performVectorSearch, type VectorSearchRequest, type VectorSearchResult } from '@/lib/vectorSearchApi'

interface UseVectorSearchReturn {
  searchResults: VectorSearchResult[]
  isSearching: boolean
  searchError: string | null
  /** Last request body passed to `performSearch` (for analytics / UI). */
  lastSearchRequest: VectorSearchRequest | null
  /** Increments once each time a search finishes (success, empty, or error). */
  searchCompletionId: number
  performSearch: (params: VectorSearchRequest) => Promise<void>
  clearSearch: () => void
}

export function useVectorSearch(): UseVectorSearchReturn {
  const [searchResults, setSearchResults] = useState<VectorSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [lastSearchRequest, setLastSearchRequest] = useState<VectorSearchRequest | null>(null)
  const [searchCompletionId, setSearchCompletionId] = useState(0)
  const completionSeqRef = useRef(0)

  const performSearch = useCallback(async (params: VectorSearchRequest) => {
    setLastSearchRequest(params)
    setIsSearching(true)
    setSearchError(null)
    setSearchResults([]) // Clear previous results immediately

    try {
      const results = await performVectorSearch(params)
      console.log('[useVectorSearch] Search completed:', {
        resultsCount: results.length,
        hasImageUrl: !!params.imageUrl,
        hasQuery: !!params.query,
        city: params.city
      })
      
      // Only set results if we have successful results
      if (results && results.length > 0) {
        setSearchResults(results)
      } else {
        setSearchResults([])
        setSearchError('No results found')
      }
    } catch (error) {
      console.error('[useVectorSearch] Search failed:', error)
      setSearchError(error instanceof Error ? error.message : 'Search failed')
      setSearchResults([])
    } finally {
      setIsSearching(false)
      completionSeqRef.current += 1
      setSearchCompletionId(completionSeqRef.current)
    }
  }, [])

  const clearSearch = useCallback(() => {
    setSearchResults([])
    setSearchError(null)
    setLastSearchRequest(null)
  }, [])

  return {
    searchResults,
    isSearching,
    searchError,
    lastSearchRequest,
    searchCompletionId,
    performSearch,
    clearSearch
  }
}
